import React, { useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { GetStaticPaths, GetStaticProps } from 'next';

import { faArrowUpFromBracket } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { TrendingTable } from '@components/Charts/Table';
import { ChartTop } from '@components/Charts/Top';
import { Header, MainContainer, PageWrapper, ShareIcons } from '@components/Charts/shared';
import { Seo } from '@components/Seo';
import { IconButton } from '@components/main-components/icon-buttons/IconButton';
import { ShareModalTop } from '@components/modals/ShareModal';
import { ModalType, useModal } from '@context/modal';
import type { TrendingArtistInfoQuery } from '@graphql/reactQuery';
import { fetchGQL, TrendingArtistInfoDocument } from '@graphql/reactQuery';
import { trackClickEvent } from '@lib/analytics';
import type { Timespan, TopArtistsSortSubpaths } from '@pages-shared/topCharts';
import {
  defaultCollectorsTopChartsPageSort,
  defaultCuratorsTopChartsPageSort,
  defaultReleasesTopChartsPageSort,
  HandleOnChartContentSelection,
  HandleOnChartTypeSelection,
  NavigateArtistsTopChartsPage,
  Panel,
  PrefetchArtistsTopChartsPage,
  PrefetchCollectorsTopChartsPage,
  PrefetchCuratorsTopChartsPage,
  PrefetchReleasesTopChartsPage,
  StyledTabs,
  TimespanToTopChartPeriodEnum,
  TOP_CHARTS_REVALIDATION,
  TopArtistsSortSubpathsToSortEnum,
  TopChartsType,
} from '@pages-shared/topCharts';

type StaticPaths = {
  timespan: Timespan;
  sort: TopArtistsSortSubpaths;
};

type PageProps = {
  topArtists: TrendingArtistInfoQuery['topArtistsV2'];
  timespan: Timespan;
  sort: TopArtistsSortSubpaths;
};

export const getStaticProps: GetStaticProps<PageProps, StaticPaths> = async ({ params }) => {
  if (!params) {
    return {
      notFound: true,
    };
  }

  const { timespan, sort } = params;

  const [topArtists] = await Promise.all([
    fetchGQL(TrendingArtistInfoDocument, {
      variables: {
        input: {
          timePeriod: TimespanToTopChartPeriodEnum[timespan],
          sort: TopArtistsSortSubpathsToSortEnum[sort],
        },
      },
    }).then(v => v.data.topArtistsV2),
  ]);

  return {
    props: {
      topArtists,
      timespan,
      sort,
    },
    revalidate: TOP_CHARTS_REVALIDATION,
  };
};

export const getStaticPaths: GetStaticPaths<StaticPaths> = () => {
  return {
    paths: [
      {
        params: {
          sort: 'mints',
          timespan: '7d',
        },
      },
      {
        params: {
          sort: 'mints',
          timespan: '30d',
        },
      },
      {
        params: {
          sort: 'mints',
          timespan: 'all',
        },
      },

      {
        params: {
          sort: 'total-volume',
          timespan: '7d',
        },
      },
      {
        params: {
          sort: 'total-volume',
          timespan: '30d',
        },
      },
      {
        params: {
          sort: 'total-volume',
          timespan: 'all',
        },
      },

      {
        params: {
          sort: 'unique-collectors',
          timespan: '7d',
        },
      },
      {
        params: {
          sort: 'unique-collectors',
          timespan: '30d',
        },
      },
      {
        params: {
          sort: 'unique-collectors',
          timespan: 'all',
        },
      },
    ],
    fallback: false,
  };
};

export default function Page({ topArtists, sort, timespan }: PageProps) {
  const { openModal } = useModal();

  useEffect(() => {
    PrefetchCollectorsTopChartsPage({
      timespan,
      sort: defaultCollectorsTopChartsPageSort,
    });
    PrefetchReleasesTopChartsPage({
      timespan,
      sort: defaultReleasesTopChartsPageSort,
    });
    PrefetchCuratorsTopChartsPage({
      timespan,
      sort: defaultCuratorsTopChartsPageSort,
    });

    PrefetchArtistsTopChartsPage({
      timespan: '30d',
      sort,
    });
    PrefetchArtistsTopChartsPage({
      timespan: '7d',
      sort,
    });
    PrefetchArtistsTopChartsPage({
      timespan: 'all',
      sort,
    });
  }, [sort, timespan]);

  return (
    <PageWrapper>
      <Seo title="Charts | Sound.xyz" description="What's trending in Sound.xyz" />
      <MainContainer>
        <Header>
          <ChartTop
            chartType="top"
            contentType="artists"
            onChartContentSelection={HandleOnChartContentSelection({ timespan })}
            onChartTypeSelection={HandleOnChartTypeSelection()}
            icons={
              <ShareIcons>
                <IconButton
                  icon={<FontAwesomeIcon icon={faArrowUpFromBracket} size="lg" />}
                  onClick={() => {
                    trackClickEvent({
                      event: 'Share Top Sounds Clicked',
                      properties: {
                        sourceComponent: 'Chart',
                      },
                    });
                    openModal(ModalType.SHARE, {
                      body: <ShareModalTop type="artists" />,
                    });
                  }}
                />
              </ShareIcons>
            }
          />
        </Header>

        <StyledTabs value={TopChartsType.artists}>
          <TrendingTable
            artistTrendingData={topArtists}
            activeTimespan={timespan}
            setActiveTimespan={timespan => {
              NavigateArtistsTopChartsPage({
                timespan,
                sort,
              });
            }}
            onTopArtistSortClick={sort => {
              NavigateArtistsTopChartsPage({
                sort,
                timespan,
              });
            }}
            sort={sort}
          />

          <Panel value="sounds" />
          <Panel value="artists" />
          <Panel value="collectors" />
        </StyledTabs>
      </MainContainer>
    </PageWrapper>
  );
}
